/**
 * UPMI bespoke storefront chrome. Uses the SHARED <Header/> and <Footer/> — real
 * search + suggestions, cart, auth-aware account/notifications, smart Track
 * Order, CMS util links, and CMS-driven footer columns/social — so nothing is
 * reimplemented and the chrome matches the transactional pages (product/cart/
 * checkout) exactly. On top of that it adds the one thing the shared header
 * lacks: the upmi page-nav strip (Home/Products/Gallery/About/Contact).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Truck } from 'lucide-react';
import { Header } from '../../../storefront/components/header';
import { Footer } from '../../../storefront/components/footer';
import { INK, LINE, MUTED, O } from './tokens';

export type UpmiPageKey = 'home' | 'products' | 'gallery' | 'about' | 'contact';

const NAV: { key: UpmiPageKey; label: string; href: string }[] = [
  { key: 'home', label: 'Home', href: '/' },
  { key: 'products', label: 'Products', href: '/storefront' },
  { key: 'gallery', label: 'Gallery', href: '/gallery' },
  { key: 'about', label: 'About Us', href: '/about-us' },
  { key: 'contact', label: 'Contact', href: '/contact' },
];

export function UpmiShell({ active, children }: { active: UpmiPageKey; children: ReactNode }) {
  return (
    <div
      className="omni-store"
      style={{
        background: '#f5f5f5',
        minHeight: '100vh',
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        color: INK,
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      {/* Shared header — `stickyTop` pins the whole top bar (util strip + search)
          on scroll; the page-nav strip below it scrolls away normally. */}
      <Header searchPlaceholder="Search magnesium, coffee, collagen, kits…" stickyTop />

      {/* upmi page-nav strip (scrolls under the pinned top header) */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${LINE}`, overflowX: 'auto' }}>
        <div
          style={{
            maxWidth: 1180,
            margin: '0 auto',
            padding: '0 16px',
            height: 44,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {NAV.map((n) => (
            <Link
              key={n.key}
              to={n.href}
              style={{
                position: 'relative',
                height: 44,
                display: 'flex',
                alignItems: 'center',
                padding: '0 14px',
                fontSize: 14,
                fontWeight: 700,
                color: INK,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {n.label}
              {active === n.key && (
                <span
                  style={{
                    position: 'absolute',
                    left: 14,
                    right: 14,
                    bottom: 0,
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    background: O,
                  }}
                />
              )}
            </Link>
          ))}
          <span
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12.5,
              color: MUTED,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <Truck size={15} color="#0a8a4f" /> Free shipping over ₱1,000 nationwide
          </span>
        </div>
      </div>

      <main>{children}</main>

      {/* Shared config-driven footer — same on bespoke + transactional pages. */}
      <Footer />
    </div>
  );
}
